import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Prompt, showNotification } from 'sonata-react-components';

import { getUserId } from '@/helpers/user/user.helpers';
import { deleteVenue } from '@/services/venue.service';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import './deleteVenues.scss';

type Props = {
  onConfirm: () => void;
  onCancel: () => void;
  user: UserInterface;
  venueIds: Venue['_id'][];
};

export function DeleteVenues({ onCancel, onConfirm, user, venueIds }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const userId = getUserId(user);
  const BASE_ID = 'deleteVenuesPrompt';

  const token = {};
  const handleConfirm = async () => {
    try {
      setIsRemoving(true);
      await venueIds.reduce(async (prev, id) => {
        await prev;
        return deleteVenue(id, userId, token);
      }, Promise.resolve());
      onConfirm();
    } catch (_) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'An error occurred while deleting the venues. Please try again.',
          id: 'menu.myVenues.remove.error'
        })
      );
    } finally {
      setIsRemoving(false);
    }
  };

  if (venueIds.length === 0) {
    return null;
  }

  return (
    <Prompt
      buttons={[
        {
          id: `${BASE_ID}_cancelBtn`,
          key: `${BASE_ID}_cancel`,
          onClick: onCancel,
          text: formatMessage({
            defaultMessage: 'Cancel',
            id: 'common.cancel'
          }),
          type: 'secondary'
        },
        {
          disabled: isRemoving,
          id: `${BASE_ID}_cofirmBtn`,
          key: `${BASE_ID}_confirm`,
          loading: isRemoving,
          onClick: handleConfirm,
          text: formatMessage({
            defaultMessage: 'Confirm',
            id: 'common.confirm'
          })
        }
      ]}
      className="deleteVenuesPrompt"
      closePrompt={onCancel}
      id={`${BASE_ID}_prompt`}
      title={formatMessage({
        defaultMessage: 'Are you sure you want to remove the selected venues?',
        id: 'menu.myVenues.remove.title'
      })}
    />
  );
}
